import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Fingerprint } from "lucide-react";
import SubhearnBank5Ekyc from "./SubhearnBank5Ekyc";
import SubhearnBank5TwoFactorAuth from "./SubhearnBank5TwoFactorAuth";
import useApi from "../../../CustomHooks/useApi";

const bankMappings = [
  { short: "SBI",     full: "State Bank of India"    },
  { short: "PNB",     full: "Punjab National Bank"   },
  { short: "BOB",     full: "BANK OF BARODA"         },
  { short: "BOI",     full: "BANK OF INDIA"          },
  { short: "UBI",     full: "UNION BANK OF INDIA"    },
  { short: "INDIAN",  full: "INDIAN BANK"            },
  { short: "CBI",     full: "CENTRAL BANK OF INDIA"  },
  { short: "CANARA",  full: "CANARA BANK"            },
  { short: "BOM",     full: "BANK OF MAHARASHTRA"    },
  { short: "PSB",     full: "PUNJAB AND SIND BANK"   },
  { short: "IOB",     full: "INDIAN OVERSEAS BANK"   },
  { short: "UCO",     full: "UCO BANK"               },
  { short: "AIRTEL",  full: "AIRTEL PAYMENTS BANK"   },
  { short: "IPPB",    full: "INDIA POST PAYMENTS BANK"},
  { short: "HDFC",    full: "HDFC Bank"              },
  { short: "ICICI",   full: "ICICI Bank"             },
  { short: "AXIS",    full: "Axis Bank"              },
  { short: "IDBI",    full: "IDBI BANK"              },
  { short: "INDUSIND",full: "IndusInd Bank"          },
  { short: "KOTAK",   full: "Kotak Mahindra Bank"    },
];

const BANK_COLORS = [
  "bg-blue-700","bg-red-600","bg-orange-600","bg-indigo-900",
  "bg-rose-600","bg-blue-800","bg-blue-600","bg-teal-600",
  "bg-sky-600","bg-orange-500","bg-indigo-700","bg-green-600",
  "bg-purple-600","bg-cyan-600","bg-red-500","bg-orange-700",
  "bg-pink-600","bg-violet-600","bg-emerald-600","bg-fuchsia-600",
];

export default function SubhearnBank5AepsForm({
  onSubmit,
  loading,
  previousData,
  processing,
  clearFormTrigger,
}) {
  const username = useSelector((s) => s.auth?.employeeDetails?.username);

  /* Bank5 banks: POST with no params (empty body) */
  const { callApi: getBanksApi }       = useApi("/aeps/subhearn/bank5/banks",        "POST");
  /* Bank5 status: POST /bank5/login-status */
  const { callApi: getLoginStatusApi } = useApi("/aeps/subhearn/bank5/login-status", "POST");

  const [banks,           setBanks]           = useState([]);
  const [banksLoading,    setBanksLoading]    = useState(false);
  const [form,            setForm]            = useState({ aadhaar: "", mobile: "", amount: "", bank: "", txnType: "CW" });
  const [is2FAVerified,   setIs2FAVerified]   = useState(false);
  const [showDaily2FAAlert,setShowDaily2FAAlert]= useState(false);
  const [showEkycRequired,setShowEkycRequired]= useState(false);
  const [kycStatusMessage,setKycStatusMessage]= useState("");
  const [showOtherBanks,  setShowOtherBanks]  = useState(false);
  const [searchTerm,      setSearchTerm]      = useState("");
  const [isDropdownOpen,  setIsDropdownOpen]  = useState(false);
  const dropdownRef = useRef(null);

  /* ── status check via bank5/login-status ── */
  const checkStatus = async () => {
    if (!username) return;
    try {
      const res = await getLoginStatusApi({ mid: username });

      /* Case 0: eKYC still required — must complete eKYC first */
      const ekycRequired = res?.ekycRequired === true || res?.data?.ekycRequired === true;
      if (ekycRequired) {
        setShowEkycRequired(true);
        setIs2FAVerified(false);
        setShowDaily2FAAlert(false);
        setKycStatusMessage("eKYC not completed. Please select E-KYC to onboard first.");
        return;
      }

      /* Case 1: Already logged in — no 2FA needed */
      if (res?.actcode === "LOGGEDIN" || res?.statuscode === "TXN" && res?.actcode === "LOGGEDIN") {
        setShowEkycRequired(false);
        setKycStatusMessage("");
        setIs2FAVerified(true);
        setShowDaily2FAAlert(false);
        return;
      }

      /* Case 2: 2FA required */
      if (res?.actcode === "LOGINREQUIRED") {
        setShowEkycRequired(false);
        setKycStatusMessage("");
        setIs2FAVerified(false);
        setShowDaily2FAAlert(true);
        return;
      }

      /* Case 3: bank activation pending (ERR from bank side) */
      if (res?.data?.statuscode === "ERR" || (res?.success === false && !res?.actcode)) {
        setShowEkycRequired(true);
        setIs2FAVerified(false);
        setShowDaily2FAAlert(false);
        setKycStatusMessage(res?.message || res?.data?.status || "Activation pending from bank. Please try again later.");
        return;
      }

      /* Fallback: any other response → treat as eKYC needed */
      setShowEkycRequired(true);
      setIs2FAVerified(false);
      setShowDaily2FAAlert(false);
      setKycStatusMessage("Please complete eKYC to proceed.");
    } catch {
      /* API error → merchant not yet onboarded / eKYC needed */
      setShowEkycRequired(true);
      setIs2FAVerified(false);
      setShowDaily2FAAlert(false);
      setKycStatusMessage("Please complete eKYC to proceed.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "bank") {
      setForm((p) => ({ ...p, bank: banks.find((b) => b.iin === value) || "" }));
      return;
    }
    if (name === "aadhaar") {
      const v = value.replace(/\D/g, "");
      if (v.length <= 12) setForm((p) => ({ ...p, aadhaar: v }));
      return;
    }
    if (name === "mobile") {
      const v = value.replace(/\D/g, "");
      if (v.length <= 10) setForm((p) => ({ ...p, mobile: v }));
      return;
    }
    if (name === "amount") {
      setForm((p) => ({ ...p, amount: value.replace(/\D/g, "") }));
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
  };

  const isFormValid =
    form.aadhaar.length === 12 &&
    form.mobile.length  === 10 &&
    form.bank &&
    (form.txnType !== "CW" || (form.amount.trim() !== "" && Number(form.amount) <= 5000));

  const handleQuickBankSelect = (bank) => {
    setForm((p) => ({ ...p, bank }));
    setShowOtherBanks(false);
  };

  const handleDropdownSelect = (bank) => {
    setForm((p) => ({ ...p, bank }));
    setSearchTerm(bank.name);
    setIsDropdownOpen(false);
  };

  const handlePreviousData = () => {
    if (!previousData) return;
    setForm((p) => ({ ...p, ...previousData, bank: previousData?.bank || "" }));
    if (previousData?.bank) setSearchTerm(previousData.bank.name || "");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isFormValid && !loading && !processing && is2FAVerified && !showEkycRequired) {
        onSubmit(form);
      }
    }
  };

  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => { checkStatus(); }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchBanks = async () => {
      setBanksLoading(true);
      try {
        /* Bank5 banks API: POST with no body */
        const res = await getBanksApi(null);
        if (res?.statuscode === "TXN" || res?.status === "SUCCESS" || Array.isArray(res?.data?.BankList) || Array.isArray(res?.data)) {
          const raw = res?.data?.BankList || res?.data || [];
          const normalised = raw.map((b) => ({
            iin:  b.iinno   ?? b.bankIin  ?? b.bankiin  ?? b.iin  ?? b.IIN  ?? "",
            name: b.details ?? b.bank_name ?? b.bankName ?? b.name ?? b.BankName ?? "",
          })).filter((b) => b.iin && b.name);
          setBanks(normalised);
        }
      } catch (e) {
        console.error("SubhearnBank5AepsForm: failed to load banks", e);
      } finally {
        setBanksLoading(false);
      }
    };
    fetchBanks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  useEffect(() => {
    if (clearFormTrigger) {
      setForm((p) => ({ ...p, bank: "", aadhaar: "", mobile: "", amount: "" }));
      setSearchTerm("");
    }
  }, [clearFormTrigger]);

  /* ── sub-page renders ── */
  if (form.txnType === "2FA") {
    return (
      <SubhearnBank5TwoFactorAuth
        onBack={() => setForm({ ...form, txnType: "CW" })}
        onSuccess={() => {
          setIs2FAVerified(true);
          setShowDaily2FAAlert(false);
          setForm((p) => ({ ...p, txnType: "CW" }));
        }}
      />
    );
  }

  if (form.txnType === "EKYC") {
    return (
      <SubhearnBank5Ekyc
        onBack={() => setForm({ ...form, txnType: "CW" })}
        onSuccess={() => {
          checkStatus();
          setForm((p) => ({ ...p, txnType: "CW" }));
        }}
      />
    );
  }

  /* ── bank quick-select ── */
  const highlightedBanks = bankMappings
    .map((item) => ({ label: item.short, bank: banks.find((b) => b.name?.toLowerCase() === item.full.toLowerCase()) }))
    .filter((x) => x.bank);

  const safeSearch      = (searchTerm || "").toLowerCase();
  const filteredDropdown = banks.filter((b) => b.name?.toLowerCase().includes(safeSearch));

  return (
    <div className="bg-[#f4f6fb]">
      <div className="ml-[12vw] -mt-14 font-semibold text-lg py-2">
        AEPS 4
      </div>

      {/* Status banners */}
      {showEkycRequired ? (
        <div className="bg-orange-50 border border-orange-300 rounded-lg text-center p-3 mb-2">
          <p className="text-orange-700 font-semibold text-base">
            {kycStatusMessage || "eKYC not completed. Please select E-KYC to onboard first."}
          </p>
        </div>
      ) : (
        showDaily2FAAlert && (
          <div className="mb-2 bg-blue-50 border p-2 rounded-lg text-center text-blue-700 text-base font-medium">
            Daily 2FA required before AEPS transactions — select 2-Factor Auth below.
          </div>
        )
      )}

      <div className="grid grid-cols-12 h-full">

        {/* ── LEFT: form ── */}
        <div
          className="col-span-5 border-r px-5 py-4 flex flex-col justify-start h-full"
          onKeyDown={handleKeyDown}
        >
          <h3 className="font-bold text-xl mb-4">Transaction Type :</h3>

          <div className="grid grid-cols-2 gap-y-3 text-base font-semibold mb-6">
            {[
              ["CW",   "Cash Withdrawal"],
              ["BE",   "Balance Enquiry"],
              ["MS",   "Mini Statement" ],
              ["EKYC", "E-KYC"          ],
              ["2FA",  "2-Factor Auth"  ],
            ].map(([val, lbl]) => (
              <label key={val} className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={form.txnType === val}
                  onChange={() => setForm({ ...form, txnType: val })}
                />
                {lbl}
              </label>
            ))}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-base font-semibold">Aadhaar Number</label>
              <input
                name="aadhaar" value={form.aadhaar} onChange={handleChange}
                maxLength={12} inputMode="numeric" placeholder="Aadhaar Number"
                className="w-full h-12 border-2 rounded-lg px-4 text-base font-bold"
              />
              {form.aadhaar && form.aadhaar.length < 12 && (
                <p className="text-red-500 text-sm mt-1">Aadhaar must be 12 digits</p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-base font-semibold">Mobile Number</label>
              <input
                name="mobile" value={form.mobile} onChange={handleChange}
                maxLength={10} inputMode="numeric" placeholder="Mobile Number"
                className="w-full h-12 border-2 rounded-lg px-4 text-base font-bold"
              />
              {form.mobile && form.mobile.length < 10 && (
                <p className="text-red-500 text-sm mt-1">Mobile must be 10 digits</p>
              )}
            </div>

            {form.txnType === "CW" && (
              <div>
                <label className="block mb-2 text-base font-semibold">Transaction Amount</label>
                <input
                  name="amount" value={form.amount} onChange={handleChange}
                  inputMode="numeric" placeholder="Transaction Amount"
                  className="w-full h-12 border-2 rounded-lg px-4 text-base"
                />
                {Number(form.amount) > 5000 && (
                  <p className="text-red-500 text-sm font-medium mt-1">
                    Please enter an amount of ₹5,000 or less
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => onSubmit(form)}
                disabled={loading || processing || !isFormValid || !is2FAVerified || showEkycRequired}
                className="h-12 bg-green-600 hover:bg-green-700 text-white rounded-lg text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Fingerprint size={20} />
                {loading ? "Capturing…" : "Capture"}
              </button>
              <button
                type="button" onClick={handlePreviousData} disabled={!previousData}
                className="h-12 bg-slate-700 text-white rounded-lg text-base font-bold disabled:opacity-50"
              >
                Previous
              </button>
            </div>
            {!is2FAVerified && !showEkycRequired && (
              <p className="text-red-500 text-sm mt-3">Please complete daily 2FA first</p>
            )}
          </div>
        </div>

        {/* ── RIGHT: bank selection ── */}
        <div className="col-span-7 px-4 py-4">
          <h3 className="font-bold text-xl mb-4">
            Bank Name :
            {banksLoading && (
              <span className="ml-2 text-sm font-normal text-gray-400 animate-pulse">Loading banks…</span>
            )}
          </h3>

          {banksLoading ? (
            <div className="grid grid-cols-5 gap-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-gray-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-3">
              {highlightedBanks.map(({ label, bank }, idx) => (
                <button
                  key={bank.iin} type="button"
                  onClick={() => handleQuickBankSelect(bank)}
                  className={`h-16 rounded-xl text-white font-bold text-sm shadow leading-tight
                    ${BANK_COLORS[idx] || "bg-gray-600"}
                    ${form.bank?.iin === bank.iin ? "ring-4 ring-green-300" : ""}`}
                >
                  <div className="text-[16px]">{label}</div>
                  <div className="text-[12px] mt-1 opacity-90">{bank.name}</div>
                </button>
              ))}
              <button
                onClick={() => {
                  setShowOtherBanks(!showOtherBanks);
                  if (!showOtherBanks && form.bank) setSearchTerm(form.bank.name || "");
                }}
                className="h-16 rounded-xl bg-blue-900 text-white font-bold text-sm shadow"
              >
                OTHER BANKS
              </button>
            </div>
          )}

          {showOtherBanks && (
            <div className="mt-4 relative" ref={dropdownRef}>
              <input
                type="text" placeholder="Search and select bank…"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setIsDropdownOpen(true); }}
                onFocus={() => setIsDropdownOpen(true)}
                className="w-full h-12 border-2 rounded-lg px-4 text-base focus:border-indigo-600 focus:outline-none bg-white"
              />
              {isDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-2xl max-h-60 overflow-y-auto">
                  {filteredDropdown.length > 0 ? (
                    filteredDropdown.map((b) => (
                      <div
                        key={b.iin} onClick={() => handleDropdownSelect(b)}
                        className="px-4 py-3 hover:bg-slate-100 cursor-pointer text-slate-700 border-b last:border-b-0 text-sm font-medium"
                      >
                        {b.name}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-slate-500 text-sm">No banks found</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
